import { composeClassNames, universalEach } from "@faedev/editor-utils";
import { useRouter } from "next/router";
import { CSSProperties, useEffect, useState } from "react";
import { Alert, Button, Col, Row } from "react-bootstrap";
import { useCookies } from "react-cookie";
import Cafeterias from "src/components/Cafeterias";
import Content from "src/components/Content";
import Preloader from "src/components/Preloader";
import PricesSubscription from "src/components/PricesSubscription";
import SubscriptionCard from "src/components/SubscriptionCard";
import Translate from "src/components/Translate";
import constants from "src/constants";
import getCookieByName from "src/functions/getCookieByName";
import gutter from "src/functions/gutter";
import showPriceInSubscriptionCard from "src/functions/showPriceInSubscriptionCard";
import translate from "src/functions/translate";
import useLoggedInUser from "src/functions/useLoggedInUser";
import validateAuthToken from "src/functions/validateAuthToken";
import Small_chevron_right from "src/icons/Small-chevron-right";
import { sAlert } from "src/styles/sAlert.module.scss";
import { sButton } from "src/styles/sButton.module.scss";
import { sSubscriptionList } from "src/styles/sSubscriptionList.module.scss";

type Props = {
  backgroundColor?: string | null | undefined;
  className?: (string | string) | null | undefined;
  ctaBtnTitle?: string | null | undefined;
  style?: CSSProperties | null | undefined;
  verticalSpace?: "none" | "sm" | "nm" | "md" | "lg";
};

function SubscriptionList({
  backgroundColor,
  className,
  ctaBtnTitle,
  style,
  verticalSpace,
}: Props) {
  const { locale: lang, push } = useRouter();
  const router = useRouter();
  const [data, setData] = useState(null);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [failed, setFailed] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingBuySubscription, setLoadingBuySubscription] = useState(false);
  const [cookies, setCookie] = useCookies(null);

  var userResult = useLoggedInUser();

  useEffect(() => {
    fetch(`${constants.Paths.apiUrl}/subscriptions?_sort=period:desc`, {
      method: "GET",
    }).then(async (res) => {
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    });
  }, []);

  useEffect(() => {
    userResult.result &&
      fetch(`${constants.Paths.apiUrl}/clients/me`, {
        method: "GET",
        headers: validateAuthToken(getCookieByName(`auth-token`)),
      }).then(async (res) => {
        if (res.ok) {
          const result = await res.json();
          const subscriptionTill = result.subscription_till;
          const today = new Date();

          if (new Date(subscriptionTill) >= today) {
            setActiveSubscription(result);
          }
        }
      });
  }, [userResult.result]);

  const handleSubmit = (id: number) => {
    const cookieOrderId = getCookieByName(`orderId`);
    fetch(`${constants.Paths.apiUrl}/orders`, {
      method: "POST",
      headers: userResult.result
        ? validateAuthToken(getCookieByName(`auth-token`), {
            "Content-Type": "application/json",
          })
        : { "Content-Type": "application/json" },
      body: JSON.stringify({
        order_id: cookieOrderId ? cookieOrderId : "",
        item: {
          entityId: id,
          entityType: "subscription",
        },
      }),
    }).then(async (res) => {
      if (res.ok) {
        setCookie(`orderId`, await res.text(), constants.CookiesOptions);
        window.location.href = "/cart";
      } else {
        setLoadingBuySubscription(false);
        let status = await res.json();
        status.message === "already in"
          ? push("/cart")
          : () => {
              setFailed(true);
              setErrorMessage(`Could not add to cart, please try again.`);
            };
      }
    });
  };

  const showSubscriptions = () => {
    const subscriptions = constants.Finance.subscriptionPrices;
    const sortedSubscriptions = subscriptions.map((subscriptionPosition) =>
      data.find(
        (subscription) => Number(subscription.id) === subscriptionPosition
      )
    );

    return universalEach(sortedSubscriptions, (sub, i) => {
      return (
        <Col xs={{ span: 12 }} sm={6} lg={{ span: 4 }} key={sub.id}>
          <SubscriptionCard
            activeId={2}
            className={
              activeSubscription &&
              activeSubscription.subscription.id === sub.id
                ? "bought"
                : null
            }
            description={sub.description}
            disableHover={true}
            id={sub.id}
            key={`sub-${sub.id}`}
            price={
              sub.id === 1
                ? showPriceInSubscriptionCard(
                    sub.price,
                    sub.currency,
                    "you get 5 months free"
                  )
                : showPriceInSubscriptionCard(
                    sub.price,
                    sub.currency,
                    "1 month membership for"
                  )
            }
            pricePage={true}
            setActive={() => {}}
            title={translate(sub.title + " subscription", lang)}
            cta={
              !activeSubscription ? (
                <Button
                  variant={`warning`}
                  className={composeClassNames([
                    sButton,
                    "btn-icon-after",
                    "btn-shine",
                  ])}
                  onClick={() => handleSubmit(sub.id)}
                >
                  {loadingBuySubscription ? (
                    <Preloader size="sm" />
                  ) : (
                    <>
                      <Translate
                        tKey={
                          ctaBtnTitle ||
                          constants.Marketing.subscriptionMembersBtnTitle
                        }
                      />
                      <Small_chevron_right />
                    </>
                  )}
                </Button>
              ) : (
                <Button
                  variant={`warning`}
                  className={composeClassNames([
                    sButton,
                    "btn-icon-after",
                    "btn-shine",
                  ])}
                  style={gutter(`margin`, `vertical`, `nm`)}
                  onClick={() => handleSubmit(sub.id)}
                >
                  <Translate tKey={"Gift your loved ones"} />
                  <Small_chevron_right />
                </Button>
              )
            }
            recommended={1}
            validTill={
              activeSubscription &&
              activeSubscription.subscription.id === sub.id &&
              activeSubscription.subscription_till
            }
          />
          {failed && (
            <Alert
              variant="danger"
              className={sAlert}
              style={gutter(`margin`, `vertical`, `md`)}
            >
              <Translate tKey={errorMessage} />
            </Alert>
          )}
        </Col>
      );
    });
  };

  return (
    <>
      {data && (
        <div
          className={composeClassNames([
            sSubscriptionList,
            `bg-${backgroundColor ? "other" : "white"}`,
            "subscription-list",
            className,
          ])}
          style={{ background: backgroundColor || constants.Theme.white }}
          id="subscription"
        >
          <Content fluid={`xl`} verticalSpace={verticalSpace || `lg`}>
            <Row>
              <Col xs={12} sm={12} lg={4} className="subscription-label">
                <PricesSubscription />
              </Col>
              {data ? (
                showSubscriptions()
              ) : (
                <div style={gutter(`margin`, `all`, `xl`)}>
                  <Preloader />
                </div>
              )}
            </Row>
            {router.pathname !== "/prices" && (
              <>
                <Button
                  variant="link"
                  className={composeClassNames([
                    sButton,
                    "subscription-other-prices",
                  ])}
                  onClick={() => push("/prices")}
                >
                  <Translate tKey={`Other pricing options`} />
                </Button>
                <Cafeterias
                  size="sm"
                  align="center"
                  color="dark"
                  style={gutter(`margin`, `top`, `md`)}
                />
              </>
            )}
          </Content>
        </div>
      )}
    </>
  );
}

export default SubscriptionList;
